/**
 * What a card charge costs, and what to charge so the shul keeps the whole
 * amount that was billed.
 *
 * A payer who covers the fee is choosing to give more, not being surcharged —
 * it is offered, off by default, and the added amount is stated before they
 * agree to it. That is what keeps it a voluntary contribution rather than a
 * card-network surcharge with the compliance that carries.
 *
 * The rate is Stripe's published pricing for a domestic card rather than a
 * number we invented. It is still an ESTIMATE: an Amex, an international card
 * or a negotiated rate all cost something else, and Stripe only reports the
 * true figure on the balance transaction after the charge has settled. So the
 * webhook records what Stripe actually took, and a persistently wrong estimate
 * shows up in the data instead of quietly costing the shul money.
 */

/** Stripe's standard rate for a domestic card charge. */
export const FEE_PERCENT = 0.029
export const FEE_FIXED_CENTS = 30

/**
 * What to charge so that `netCents` survives the fee.
 *
 * Solving `gross - (gross × percent + fixed) = net` gives
 * `gross = (net + fixed) / (1 - percent)`. It is NOT `net × 1.029`: adding the
 * percentage to the net undercharges, because the fee is taken on the larger
 * figure. At $310 that mistake leaves the shul about 30¢ short, and it grows
 * with the amount.
 *
 * Rounded UP, so rounding can only ever leave the shul whole rather than a cent
 * short of the sum it billed.
 */
export function grossUp(netCents: number): number {
  if (netCents <= 0) return 0
  return Math.ceil((netCents + FEE_FIXED_CENTS) / (1 - FEE_PERCENT))
}

/** What covering the fee adds to what the payer owes — the offer, in one number. */
export function surchargeFor(netCents: number): number {
  if (netCents <= 0) return 0
  return grossUp(netCents) - netCents
}

/** What Stripe is expected to take on a charge of this size. */
export function estimatedFee(grossCents: number): number {
  if (grossCents <= 0) return 0
  return Math.round(grossCents * FEE_PERCENT) + FEE_FIXED_CENTS
}
